import React from 'react';
import { motion } from 'framer-motion';
import {
  Check,
  Zap,
  ChevronDown,
  Shield,
  CalendarCheck,
  Bot,
  Megaphone,
  BarChart3,
  MessageCircle,
} from 'lucide-react';
import { FAQ_PRICING, WHATSAPP_LINK, CONTACT_PHONE } from '../constants';

const SERVICE_GROUPS = [
  {
    title: 'Reservas y citas',
    icon: CalendarCheck,
    items: [
      'Reservas automáticas por WhatsApp 24/7',
      'Recordatorios que reducen no-shows un 70%',
      'Sincronización con tu calendario',
      'Confirmación y reprogramación automática',
    ],
  },
  {
    title: 'Atención al cliente',
    icon: Bot,
    items: [
      'Asistente IA que contesta por WhatsApp e Instagram',
      'Respuestas a preguntas frecuentes sin intervención',
      'Multi-idioma, respuesta en menos de 1 segundo',
      'Transferencia a humano cuando la IA no sabe',
    ],
  },
  {
    title: 'Marketing con IA',
    icon: Megaphone,
    items: [
      'Posts diarios en Instagram, Facebook y Google Business',
      'Videos IA para Reels y TikTok',
      'Campañas WhatsApp con 90% de apertura',
      'Email marketing automatizado',
    ],
  },
  {
    title: 'Reputación y CRM',
    icon: BarChart3,
    items: [
      'Gestión y respuesta a reseñas de Google',
      'CRM de clientes con historial completo',
      'Seguimiento post-visita automático',
      'Informes y recomendaciones personalizadas',
    ],
  },
  {
    title: 'Extras avanzados',
    icon: Zap,
    items: [
      'Agente de voz IA que atiende llamadas',
      'Consentimientos GDPR digitales con firma electrónica',
      'Blog SEO con artículos generados por IA',
      'Llamadas salientes automatizadas',
    ],
  },
];

const FAQItem: React.FC<{ question: string; answer: string; index: number }> = ({
  question,
  answer,
  index,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="rounded-xl overflow-hidden transition-all duration-200"
      style={{
        background: '#ffffff',
        border: isOpen ? '1px solid rgba(60,179,113,0.3)' : '1px solid rgba(0,0,0,0.07)',
        boxShadow: isOpen
          ? '0 4px 20px rgba(60,179,113,0.08)'
          : '0 1px 4px rgba(0,0,0,0.04)',
      }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 text-left transition-colors"
        style={{ background: isOpen ? 'rgba(60,179,113,0.03)' : 'transparent' }}
      >
        <span className="font-bold text-slate-800 pr-4">{question}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300"
          style={{
            background: isOpen ? 'rgba(60,179,113,0.1)' : 'rgba(0,0,0,0.04)',
          }}
        >
          <ChevronDown size={16} style={{ color: isOpen ? '#3CB371' : '#94a3b8' }} />
        </motion.div>
      </button>
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div
          className="p-5 pt-0 text-slate-500 text-sm leading-relaxed"
          style={{ borderTop: '1px solid rgba(0,0,0,0.05)' }}
        >
          {answer}
        </div>
      </div>
    </motion.div>
  );
};

export const PricingSection: React.FC = () => {
  return (
    <section
      id="pricing"
      className="py-24 border-t border-slate-100 relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #faf8ff 0%, #f2f3ff 50%, #faf8ff 100%)',
      }}
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-emerald-400/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 py-1.5 px-4 rounded-full text-sm font-bold mb-6 uppercase tracking-widest"
            style={{
              background: 'rgba(60,179,113,0.1)',
              border: '1px solid rgba(60,179,113,0.4)',
              color: '#2d8653',
            }}
          >
            <Zap size={14} className="text-emerald-500" />
            Setup gratis en todos los planes
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-black text-slate-900 mb-6 tracking-tight leading-tight"
          >
            Precios claros,{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #3CB371, #1a8a52)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              sin sorpresas
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-500 font-light leading-relaxed"
          >
            Te hacemos un presupuesto a medida. Empiezas desde 9,99€/mes y añadimos solo lo que necesites.
          </motion.p>
        </div>

        {/* Price hero card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto mb-16"
        >
          <div
            className="rounded-3xl overflow-hidden relative"
            style={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
              boxShadow: '0 25px 60px rgba(15,23,42,0.3), 0 0 80px rgba(60,179,113,0.08)',
            }}
          >
            {/* Top glow bar */}
            <div
              className="h-1.5 w-full"
              style={{ background: 'linear-gradient(90deg, #3CB371, #1a8a52, #3CB371)' }}
            />
            
            <div className="p-8 md:p-12">
              {/* Badge */}
              <div className="text-center mb-8">
                <span
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold"
                  style={{
                    background: 'linear-gradient(135deg, #f97316, #ea580c)',
                    boxShadow: '0 4px 15px rgba(249,115,22,0.4)',
                    color: '#ffffff',
                  }}
                >
                  🚀 Precio de lanzamiento
                </span>
              </div>

              {/* Price */}
              <div className="text-center mb-8">
                <div className="flex items-baseline justify-center gap-1">
                  <span
                    className="text-6xl md:text-8xl font-black leading-none"
                    style={{
                      background: 'linear-gradient(135deg, #3CB371 0%, #7ee8a2 50%, #22d3ee 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      filter: 'drop-shadow(0 0 30px rgba(60,179,113,0.4))',
                    }}
                  >
                    9,99€
                  </span>
                  <span className="text-xl md:text-2xl font-medium text-slate-400">/mes</span>
                </div>
                <p className="text-slate-400 text-sm mt-3 font-light">
                  Setup gratis · Sin permanencia · Garantía 30 días
                </p>
              </div>

              {/* Flexible pricing explanation */}
              <div className="max-w-2xl mx-auto mb-10 text-center">
                <p className="text-slate-300 text-base leading-relaxed mb-6">
                  Implementamos <strong className="text-white">exactamente lo que tu negocio necesita</strong>. 
                  Empiezas desde 9,99€/mes y escalamos según tus objetivos: más canales, más automatizaciones, más alcance.
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  {[
                    { label: 'Base', desc: 'WhatsApp IA + reservas' },
                    { label: 'Crece', desc: '+ marketing + CRM' },
                    { label: 'Escala', desc: '+ voz + extras' },
                  ].map((step) => (
                    <div
                      key={step.label}
                      className="rounded-xl p-4 text-left"
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                      }}
                    >
                      <div className="text-emerald-400 text-xs font-bold uppercase tracking-wider mb-1">
                        {step.label}
                      </div>
                      <div className="text-slate-300 text-sm">{step.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={`${WHATSAPP_LINK}?text=${encodeURIComponent('Hola, quiero que me hagáis un presupuesto para mi negocio')}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-white font-bold text-base transition-all duration-200 hover:-translate-y-0.5"
                  style={{
                    background: 'linear-gradient(135deg, #25D366, #128C7E)',
                    boxShadow: '0 4px 25px rgba(37,211,102,0.35)',
                  }}
                >
                  <MessageCircle size={18} />
                  Pedir presupuesto por WhatsApp
                </a>
                <a
                  href={`tel:${CONTACT_PHONE.replace(/\s/g, '')}`}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-white/80 font-medium text-base border border-white/20 hover:bg-white/10 transition-all duration-200"
                >
                  📞 O llámanos
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Service groups */}
        <div className="max-w-4xl mx-auto space-y-6 mb-16">
          <h3 className="text-2xl font-black text-slate-900 text-center mb-8 tracking-tight">
            Todo lo que necesitas, incluido
          </h3>
          {SERVICE_GROUPS.map((group, gi) => {
            const Icon = group.icon;
            return (
              <motion.div
                key={group.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: gi * 0.08 }}
                className="rounded-2xl p-6 md:p-8"
                style={{
                  background: '#ffffff',
                  border: '1px solid rgba(0,0,0,0.07)',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                }}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: 'rgba(60,179,113,0.12)' }}
                  >
                    <Icon size={20} style={{ color: '#3CB371' }} />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900">{group.title}</h4>
                </div>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                  {group.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <div
                        className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center"
                        style={{ background: 'rgba(60,179,113,0.12)' }}
                      >
                        <Check size={11} style={{ color: '#3CB371' }} />
                      </div>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        {/* Guarantee */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="rounded-3xl p-8 md:p-12 max-w-4xl mx-auto text-center mb-20 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #f0fdf4 100%)',
            border: '1px solid rgba(60,179,113,0.3)',
            boxShadow: '0 0 40px rgba(60,179,113,0.08), inset 0 1px 0 rgba(255,255,255,0.8)',
          }}
        >
          <div
            className="absolute top-0 left-0 w-full h-1 rounded-t-3xl"
            style={{ background: 'linear-gradient(90deg, #3CB371, #1a8a52)' }}
          />
          <div
            className="inline-flex items-center justify-center w-20 h-20 rounded-full shadow-lg mb-6"
            style={{
              background: 'linear-gradient(135deg, #3CB371, #1a8a52)',
              boxShadow: '0 8px 30px rgba(60,179,113,0.3)',
            }}
          >
            <Shield size={36} className="text-white" />
          </div>
          <h3 className="text-2xl font-black text-slate-900 mb-4 tracking-tight font-display">
            Garantía de satisfacción 30 días
          </h3>
          <p className="text-lg text-slate-700 max-w-2xl mx-auto mb-6 font-medium leading-relaxed">
            Si al terminar el primer mes no estás satisfecho, cancelas y te devolvemos la
            mensualidad integra. Sin preguntas, sin permanencias, sin letra pequeña.
          </p>
          <p className="text-sm text-slate-500 italic">
            Lo hacemos porque cuando veas tu negocio funcionando solo, no vas a querer volver atrás.
          </p>
        </motion.div>

        {/* FAQ integrada */}
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h3 className="text-2xl font-black text-slate-900 mb-8 text-center tracking-tight">
              Preguntas frecuentes
            </h3>
          </div>
          <div className="space-y-3">
            {FAQ_PRICING.map((faq, idx) => (
              <FAQItem
                key={idx}
                question={faq.question}
                answer={faq.answer}
                index={idx}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};